import React from 'react';
import { cn } from '../lib/utils';
import Reveal from './Reveal';
import EditableText from './EditableText';

/**
 * Standard section header — eyebrow + title + subtitle.
 * Pass *Key props to enable live edit (admin + mode édition).
 */
const SectionHeader = ({
  eyebrow,
  eyebrowKey,
  title,
  titleKey,
  subtitle,
  subtitleKey,
  align = 'center',
  className,
  mb = 'mb-[var(--section-header-mb)]',
  reveal = false,
  revealAnimation = 'fade-up',
  revealDelay,
  titleAs: TitleTag = 'h2',
  titleClassName,
  subtitleClassName,
  eyebrowClassName,
  multilineSubtitle = true,
  formatTitle,
}) => {
  const titleClasses = cn(
    'text-[1.75rem] sm:text-3xl lg:text-[2.35rem] font-bold text-slate-900 tracking-tight leading-[1.15]',
    titleClassName
  );

  const titleNode =
    titleKey && typeof title === 'string' ? (
      <EditableText
        editKey={titleKey}
        defaultValue={title}
        as={TitleTag}
        className={titleClasses}
        formatDisplay={formatTitle}
      />
    ) : (
      <TitleTag className={titleClasses}>
        {typeof formatTitle === 'function' && typeof title === 'string'
          ? formatTitle(title)
          : title}
      </TitleTag>
    );

  const titleRule = (
    <span
      className={cn('section-title-rule', align === 'center' && 'section-title-rule--center')}
      aria-hidden="true"
    />
  );

  const subtitleNode =
    subtitle == null || subtitle === false
      ? null
      : subtitleKey && typeof subtitle === 'string' ? (
          <EditableText
            editKey={subtitleKey}
            defaultValue={subtitle}
            as="p"
            multiline={multilineSubtitle}
            className={cn(
              'text-slate-600 text-base sm:text-lg leading-relaxed',
              align === 'center' && 'max-w-2xl mx-auto',
              subtitleClassName
            )}
          />
        ) : (
          <p
            className={cn(
              'text-slate-600 text-base sm:text-lg leading-relaxed',
              align === 'center' && 'max-w-2xl mx-auto',
              subtitleClassName
            )}
          >
            {subtitle}
          </p>
        );

  const eyebrowNode =
    eyebrow == null || eyebrow === false
      ? null
      : eyebrowKey && typeof eyebrow === 'string' ? (
          <EditableText
            editKey={eyebrowKey}
            defaultValue={eyebrow}
            as="p"
            className={cn(
              'text-[#3a00e0] text-xs sm:text-sm font-semibold uppercase tracking-[0.14em] mb-3',
              eyebrowClassName
            )}
          />
        ) : (
          <p
            className={cn(
              'text-[#3a00e0] text-xs sm:text-sm font-semibold uppercase tracking-[0.14em] mb-3',
              eyebrowClassName
            )}
          >
            {eyebrow}
          </p>
        );

  const content = (
    <>
      {eyebrowNode}
      {titleNode}
      {titleRule}
      {subtitleNode}
    </>
  );

  const wrapClass = cn(align === 'center' && 'text-center', mb, className);

  if (reveal) {
    return (
      <Reveal animation={revealAnimation} delay={revealDelay} className={wrapClass}>
        {content}
      </Reveal>
    );
  }

  return <div className={wrapClass}>{content}</div>;
};

export default SectionHeader;
